import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Chip,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalHeader,
  Spinner,
  Table,
  TableBody,
  TableCell,
  TableColumn,
  TableHeader,
  TableRow
} from '@nextui-org/react'
import { ReactNode, memo, useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'

import {
  useSuperAdminUsageEvents,
  useSuperAdminUsageEventsFacets
} from '~/hooks/super-admin/useUsageEvents'
import { UsageEventItem, UsageEventSource } from '~/services/superAdminService'

const DAY_OPTIONS = [1, 7, 30, 90] as const
type DayOption = (typeof DAY_OPTIONS)[number]
const DEFAULT_DAYS: DayOption = 7

// Search input debounces by this much before firing a query / writing to
// the URL. Long enough that mid-typing pauses don't fire wasted requests,
// short enough that the table feels responsive once the user stops.
const SEARCH_DEBOUNCE_MS = 250

/**
 * Search input isolated as its own memoized component. The big perf
 * win: the typed value never lives on the parent, so each keystroke
 * only re-renders this small <Input>, not the whole tab (which has up
 * to 200 table rows). Parent only learns about the new value after the
 * debounce settles, via the `onDebouncedChange` callback.
 *
 * `defaultValue` is read once on mount as the seed (e.g. from the URL).
 * To clear the input from outside (Clear filters button), the parent
 * remounts via a changing `key` prop.
 */
interface DebouncedSearchInputProps {
  defaultValue: string
  placeholder: string
  delayMs: number
  // eslint-disable-next-line no-unused-vars
  onDebouncedChange: (value: string) => void
}

const DebouncedSearchInput = memo(function DebouncedSearchInput({
  defaultValue,
  placeholder,
  delayMs,
  onDebouncedChange
}: DebouncedSearchInputProps) {
  const [value, setValue] = useState(defaultValue)

  useEffect(() => {
    // Skip the initial-mount fire when the seed already matches what the
    // parent has — avoids an unnecessary parent re-render on first paint.
    if (value === defaultValue) return
    const handle = setTimeout(() => onDebouncedChange(value), delayMs)
    return () => clearTimeout(handle)
    // We deliberately omit `defaultValue` from the deps: it's only the
    // initial seed, not a controlled value, and including it would cause
    // a feedback loop with the parent's setSearch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, delayMs, onDebouncedChange])

  return (
    <Input
      size='sm'
      placeholder={placeholder}
      value={value}
      onValueChange={setValue}
      className='max-w-md'
    />
  )
})

const SOURCE_LABEL: Record<UsageEventSource, string> = {
  mcp: 'MCP / API key',
  internal: 'Internal',
  user: 'User',
  system: 'System / worker'
}

const SOURCE_COLOR: Record<
  UsageEventSource,
  'primary' | 'secondary' | 'success' | 'warning' | 'danger' | 'default'
> = {
  mcp: 'secondary',
  internal: 'warning',
  user: 'primary',
  system: 'default'
}

// Order matters — drives the chip row order in the UI. mcp first
// (matches existing convention), then internal/user/system.
const SOURCE_OPTIONS: UsageEventSource[] = ['mcp', 'internal', 'user', 'system']

const isDayOption = (n: number): n is DayOption => (DAY_OPTIONS as readonly number[]).includes(n)

const isSource = (s: string | null | undefined): s is UsageEventSource =>
  s === 'mcp' || s === 'internal' || s === 'user' || s === 'system'

const formatRelative = (unix: number): string => {
  const seconds = Math.floor(Date.now() / 1000 - unix)
  if (seconds < 60) return `${Math.max(seconds, 0)}s ago`
  const minutes = Math.floor(seconds / 60)
  if (minutes < 60) return `${minutes}m ago`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours}h ago`
  const days = Math.floor(hours / 24)
  if (days < 30) return `${days}d ago`
  const months = Math.floor(days / 30)
  return `${months}mo ago`
}

const formatAbsolute = (unix: number): string => new Date(unix * 1000).toLocaleString()

const md = (item: UsageEventItem, key: string): unknown => {
  const meta = item.metadata
  if (!meta || typeof meta !== 'object') return undefined
  return (meta as Record<string, unknown>)[key]
}
const asString = (v: unknown): string | null => (typeof v === 'string' && v.length > 0 ? v : null)
const asNumber = (v: unknown): number | null =>
  typeof v === 'number' && Number.isFinite(v) ? v : null

/**
 * Compact preview chips for the Detail column. Each metric_name surfaces
 * the fields a super-admin most likely wants to see while scanning, then
 * we always tack on conv #N / task #N when those are present so cross-row
 * cross-references are visible without opening the modal.
 */
const buildDetailPieces = (item: UsageEventItem): ReactNode[] => {
  const pieces: ReactNode[] = []

  // Internal rows are flagged regardless of metric_name. Show up front so
  // super-admins can identify them at a glance before reading other fields.
  if (item.is_internal) {
    pieces.push(
      <Chip key='internal' size='sm' variant='flat' color='warning'>
        internal
      </Chip>
    )
  }

  // mcp_call surfaces tool / status / latency directly out of the
  // top-level fields the API extracted from metadata for us.
  if (item.client_name) {
    pieces.push(
      <Chip key='client' size='sm' variant='flat' color='secondary'>
        {item.client_name}
      </Chip>
    )
  }
  if (item.tool_name) {
    pieces.push(
      <span key='tool' className='font-mono'>
        {item.tool_name}
      </span>
    )
  }
  if (item.status) {
    pieces.push(
      <Chip
        key='status'
        size='sm'
        variant='flat'
        color={item.status === 'ok' ? 'success' : 'danger'}
      >
        {item.status}
      </Chip>
    )
  }
  if (item.latency_ms !== null) {
    pieces.push(
      <span key='lat' className='tabular-nums text-tokens-neutral-text-secondary'>
        {item.latency_ms}ms
      </span>
    )
  }

  // Per-metric metadata previews — each branch reads the fields the
  // emitter is known to populate, with safe type guards. Unknown
  // metric_names just fall through to the universal refs at the bottom.
  if (item.metric_name === 'llm_tokens') {
    const model = asString(md(item, 'model_name'))
    const inT = asNumber(md(item, 'input_tokens'))
    const outT = asNumber(md(item, 'output_tokens'))
    // MCP attribution — stamped by the agent when the task was submitted
    // via the MCP API. Mirrors the chip treatment for mcp_call rows above.
    const mcpClient = asString(md(item, 'mcp_client_name'))
    const mcpTool = asString(md(item, 'mcp_tool_name'))
    if (model) {
      pieces.push(
        <span key='model' className='font-mono'>
          {model}
        </span>
      )
    }
    if (mcpClient) {
      pieces.push(
        <Chip key='mcp-client' size='sm' variant='flat' color='secondary'>
          {mcpClient}
        </Chip>
      )
    }
    if (mcpTool) {
      pieces.push(
        <span key='mcp-tool' className='font-mono'>
          {mcpTool}
        </span>
      )
    }
    if (inT !== null && outT !== null) {
      pieces.push(
        <span key='tokens' className='tabular-nums text-tokens-neutral-text-secondary'>
          {inT.toLocaleString()} in / {outT.toLocaleString()} out
        </span>
      )
    } else if (item.metric_value > 0) {
      pieces.push(
        <span key='tokens' className='tabular-nums text-tokens-neutral-text-secondary'>
          {item.metric_value.toLocaleString()} tokens
        </span>
      )
    }
  } else if (item.metric_name === 'billable_flows') {
    const ws = asString(md(item, 'workspace_name'))
    const repo = asString(md(item, 'repo_name'))
    if (ws) {
      pieces.push(
        <span key='ws' className='text-tokens-neutral-text-secondary'>
          {ws}
        </span>
      )
    }
    if (repo) {
      pieces.push(
        <span key='repo' className='font-mono'>
          {repo}
        </span>
      )
    }
    if (item.metric_value > 0) {
      pieces.push(
        <span key='flows' className='tabular-nums text-tokens-neutral-text-secondary'>
          {item.metric_value} flow{item.metric_value === 1 ? '' : 's'}
        </span>
      )
    }
  } else if (item.metric_name === 'task_billing_snapshot') {
    const billingId = asNumber(md(item, 'billing_id'))
    const used = asNumber(md(item, 'credits_used_at_start'))
    const limit = asNumber(md(item, 'credits_limit'))
    if (billingId !== null) {
      pieces.push(
        <span key='billing' className='text-tokens-neutral-text-secondary'>
          billing #{billingId}
        </span>
      )
    }
    if (used !== null && limit !== null) {
      pieces.push(
        <span key='credits' className='tabular-nums text-tokens-neutral-text-secondary'>
          {used.toLocaleString()} / {limit.toLocaleString()} credits
        </span>
      )
    }
  }

  // Universal cross-references — useful for ANY row regardless of metric.
  if (item.conversation_id !== null) {
    pieces.push(
      <span key='conv' className='text-tokens-neutral-text-secondary'>
        conv #{item.conversation_id}
      </span>
    )
  }
  if (item.task_id !== null) {
    pieces.push(
      <span key='task' className='text-tokens-neutral-text-secondary'>
        task #{item.task_id}
      </span>
    )
  }

  return pieces
}

interface DetailModalProps {
  event: UsageEventItem | null
  onClose: () => void
}

const DetailModal = ({ event, onClose }: DetailModalProps) => {
  const navigate = useNavigate()
  const goToTask = (taskId: number) => {
    onClose()
    navigate(`/super-admin/tasks?findTaskId=${taskId}`)
  }
  return (
    <Modal isOpen={event !== null} onClose={onClose} size='3xl' scrollBehavior='inside'>
      <ModalContent>
        <ModalHeader className='flex flex-col gap-1'>
          <span className='text-lg font-semibold'>Usage event #{event?.id}</span>
          {event && (
            <span className='text-xs text-tokens-neutral-text-secondary font-mono'>
              {event.metric_name} · {SOURCE_LABEL[event.source]} ·{' '}
              {formatAbsolute(event.created_at_unix)}
            </span>
          )}
        </ModalHeader>
        <ModalBody className='pb-6'>
          {!event ? null : (
            <div className='flex flex-col gap-4'>
              <Card shadow='sm' radius='sm' className='bg-tokens-neutral-bg-white'>
                <CardHeader className='p-3 border-b border-tokens-neutral-border-2 text-sm font-medium'>
                  Identity
                </CardHeader>
                <CardBody className='p-3 text-sm'>
                  <dl className='grid grid-cols-2 gap-x-3 gap-y-2'>
                    <dt className='text-tokens-neutral-text-secondary'>Org</dt>
                    <dd>
                      {event.org_name ?? `id ${event.org_id}`} · id {event.org_id}
                    </dd>
                    <dt className='text-tokens-neutral-text-secondary'>User</dt>
                    <dd>{event.user_email ?? (event.user_id ? `id ${event.user_id}` : '—')}</dd>
                    <dt className='text-tokens-neutral-text-secondary'>API token</dt>
                    <dd className='font-mono text-xs'>
                      {event.api_token_truncated ??
                        (event.api_token_id ? `id ${event.api_token_id}` : '—')}
                    </dd>
                    <dt className='text-tokens-neutral-text-secondary'>Client</dt>
                    <dd>
                      {event.client_name ? (
                        <div className='flex flex-col gap-0.5'>
                          <span>{event.client_name}</span>
                          {event.client_user_agent && (
                            <span className='font-mono text-xs text-tokens-neutral-text-secondary break-all'>
                              {event.client_user_agent}
                            </span>
                          )}
                        </div>
                      ) : (
                        '—'
                      )}
                    </dd>
                    <dt className='text-tokens-neutral-text-secondary'>Conversation</dt>
                    <dd>{event.conversation_id ?? '—'}</dd>
                    <dt className='text-tokens-neutral-text-secondary'>Task</dt>
                    <dd>
                      {event.task_id !== null ? (
                        <button
                          type='button'
                          onClick={() => goToTask(event.task_id as number)}
                          className='text-tokens-status-link-2 hover:underline focus:underline focus:outline-none'
                          aria-label={`Open task ${event.task_id} in Conversations / Tasks tab`}
                        >
                          {event.task_id}
                        </button>
                      ) : (
                        '—'
                      )}
                    </dd>
                  </dl>
                </CardBody>
              </Card>

              <Card shadow='sm' radius='sm' className='bg-tokens-neutral-bg-white'>
                <CardHeader className='p-3 border-b border-tokens-neutral-border-2 text-sm font-medium'>
                  Metric
                </CardHeader>
                <CardBody className='p-3 text-sm'>
                  <dl className='grid grid-cols-2 gap-x-3 gap-y-2'>
                    <dt className='text-tokens-neutral-text-secondary'>Name</dt>
                    <dd className='font-mono'>{event.metric_name}</dd>
                    <dt className='text-tokens-neutral-text-secondary'>Value</dt>
                    <dd className='tabular-nums'>{event.metric_value}</dd>
                    {event.tool_name && (
                      <>
                        <dt className='text-tokens-neutral-text-secondary'>Tool</dt>
                        <dd className='font-mono'>{event.tool_name}</dd>
                      </>
                    )}
                    {event.status && (
                      <>
                        <dt className='text-tokens-neutral-text-secondary'>Status</dt>
                        <dd>{event.status}</dd>
                      </>
                    )}
                    {event.latency_ms !== null && (
                      <>
                        <dt className='text-tokens-neutral-text-secondary'>Latency</dt>
                        <dd className='tabular-nums'>{event.latency_ms} ms</dd>
                      </>
                    )}
                  </dl>
                </CardBody>
              </Card>

              <Card shadow='sm' radius='sm' className='bg-tokens-neutral-bg-white'>
                <CardHeader className='p-3 border-b border-tokens-neutral-border-2 text-sm font-medium'>
                  Raw metadata
                </CardHeader>
                <CardBody className='p-3'>
                  <pre className='text-xs font-mono whitespace-pre-wrap break-all max-h-80 overflow-auto'>
                    {JSON.stringify(event.metadata, null, 2)}
                  </pre>
                </CardBody>
              </Card>
            </div>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  )
}

const UsageEventsTab = () => {
  // ── URL-persisted state ──────────────────────────────────────────
  // Filters survive page refresh and are bookmarkable. Anything the user
  // can change reflects into ?days=…&source=…&metric=…&q=… so links to
  // a specific filtered view are shareable with other super-admins.
  const [searchParams, setSearchParams] = useSearchParams()

  const initialDays = useMemo(() => {
    const raw = parseInt(searchParams.get('days') ?? '', 10)
    return isDayOption(raw) ? raw : DEFAULT_DAYS
  }, [searchParams])
  const initialSource = useMemo<UsageEventSource | null>(() => {
    const raw = searchParams.get('source')
    return isSource(raw) ? raw : null
  }, [searchParams])
  const initialMetrics = useMemo(() => new Set(searchParams.getAll('metric')), [searchParams])
  const initialSearch = searchParams.get('q') ?? ''

  const [days, setDays] = useState<DayOption>(initialDays)
  const [source, setSource] = useState<UsageEventSource | null>(initialSource)
  const [selectedMetrics, setSelectedMetrics] = useState<Set<string>>(initialMetrics)
  // Parent only stores the *debounced* search. The text input itself owns
  // its raw per-keystroke value inside <DebouncedSearchInput> and only
  // hands the value back here once it has settled, so typing doesn't
  // re-render the whole tab + table on every character.
  const [search, setSearch] = useState(initialSearch)
  // Bumped when the parent wants to force-clear the input from outside
  // (Clear filters button) — used as a key on the input so it remounts
  // and resets its internal value.
  const [searchResetKey, setSearchResetKey] = useState(0)
  const [selectedEvent, setSelectedEvent] = useState<UsageEventItem | null>(null)

  const handleSearchChange = useCallback((value: string) => {
    setSearch(value)
  }, [])

  // Sync state → URL params (one-directional; the URL is the read-on-mount
  // source of truth and writes flow back so refreshes stay sticky). The
  // search portion only updates when the debounce settles, so typing
  // doesn't churn the address bar.
  useEffect(() => {
    setSearchParams(
      prev => {
        const params = new URLSearchParams(prev)
        if (days !== DEFAULT_DAYS) params.set('days', String(days))
        else params.delete('days')
        if (source) params.set('source', source)
        else params.delete('source')
        params.delete('metric')
        for (const m of selectedMetrics) params.append('metric', m)
        if (search) params.set('q', search)
        else params.delete('q')
        return params
      },
      { replace: true }
    )
  }, [days, source, selectedMetrics, search, setSearchParams])

  const sinceUnix = useMemo(() => Math.floor(Date.now() / 1000) - days * 86_400, [days])

  const { facets, isLoading: facetsLoading } = useSuperAdminUsageEventsFacets(days)

  const {
    items,
    isLoading,
    isError,
    error,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage
  } = useSuperAdminUsageEvents({
    metricNames: selectedMetrics.size > 0 ? Array.from(selectedMetrics) : undefined,
    source: source ?? undefined,
    // `search` is already the debounced value (the input child only hands
    // it back here after it settles).
    search: search || undefined,
    sinceUnix,
    limit: 200
  })

  const toggleMetric = useCallback((m: string) => {
    setSelectedMetrics(prev => {
      const next = new Set(prev)
      if (next.has(m)) next.delete(m)
      else next.add(m)
      return next
    })
  }, [])

  const clearFilters = useCallback(() => {
    setSource(null)
    setSelectedMetrics(new Set())
    setSearch('')
    // Force the input child to remount with the empty seed so its
    // internal value resets too.
    setSearchResetKey(k => k + 1)
  }, [])

  // `search` is already the debounced/applied value, so this matches
  // what the user sees in the table — no flicker mid-typing.
  const hasActiveFilters = source !== null || selectedMetrics.size > 0 || search.length > 0

  const totalInWindow = facets ? Object.values(facets.by_source).reduce((a, b) => a + b, 0) : null

  return (
    <div className='flex flex-col gap-4'>
      <Card shadow='sm' radius='sm' className='bg-tokens-neutral-bg-white'>
        <CardHeader className='p-4 border-b border-tokens-neutral-border-2 flex flex-col gap-3'>
          <div className='flex items-center justify-between w-full gap-3'>
            <div>
              <h2 className='text-lg font-semibold'>Usage events</h2>
              <p className='text-sm text-tokens-neutral-text-secondary'>
                Every row recorded in <code className='font-mono text-xs'>usage_events</code>: MCP /
                API-key calls, user-driven actions, and system-emitted snapshots.
              </p>
            </div>
            <div className='flex items-center gap-2'>
              {DAY_OPTIONS.map(d => (
                <Button
                  key={d}
                  size='sm'
                  variant={d === days ? 'solid' : 'flat'}
                  color={d === days ? 'primary' : 'default'}
                  onPress={() => setDays(d)}
                  aria-label={`Window: ${d === 1 ? '24h' : `${d}d`}`}
                  aria-pressed={d === days}
                >
                  {d === 1 ? '24h' : `${d}d`}
                </Button>
              ))}
              <Button size='sm' variant='flat' onPress={() => refetch()}>
                Refresh
              </Button>
            </div>
          </div>

          {/* Source chips */}
          <div className='flex items-center gap-2 flex-wrap'>
            <span className='text-xs text-tokens-neutral-text-secondary'>Source:</span>
            <button
              type='button'
              onClick={() => setSource(null)}
              className='text-xs'
              aria-pressed={source === null}
              aria-label='Filter source: all'
            >
              <Chip
                size='sm'
                variant={source === null ? 'solid' : 'flat'}
                color={source === null ? 'primary' : 'default'}
              >
                All ({totalInWindow ?? '…'})
              </Chip>
            </button>
            {SOURCE_OPTIONS.map(s => (
              <button
                key={s}
                type='button'
                onClick={() => setSource(source === s ? null : s)}
                className='text-xs'
                aria-pressed={source === s}
                aria-label={`Filter source: ${s}`}
              >
                <Chip size='sm' variant={source === s ? 'solid' : 'flat'} color={SOURCE_COLOR[s]}>
                  {SOURCE_LABEL[s]} ({facets ? (facets.by_source[s] ?? 0) : '…'})
                </Chip>
              </button>
            ))}
          </div>

          {/* Metric chips */}
          <div className='flex items-center gap-2 flex-wrap'>
            <span className='text-xs text-tokens-neutral-text-secondary'>Metric:</span>
            {facetsLoading && <Spinner size='sm' />}
            {!facetsLoading && (facets?.by_metric ?? []).length === 0 && (
              <span className='text-xs text-tokens-neutral-text-secondary'>
                No events in window.
              </span>
            )}
            {(facets?.by_metric ?? []).map(m => {
              const active = selectedMetrics.has(m.metric_name)
              return (
                <button
                  key={m.metric_name}
                  type='button'
                  onClick={() => toggleMetric(m.metric_name)}
                  className='text-xs'
                  aria-pressed={active}
                  aria-label={`Filter metric: ${m.metric_name}`}
                >
                  <Chip
                    size='sm'
                    variant={active ? 'solid' : 'flat'}
                    color={active ? 'primary' : 'default'}
                  >
                    <span className='font-mono'>{m.metric_name}</span> ({m.count})
                  </Chip>
                </button>
              )
            })}
          </div>

          <div className='flex items-center gap-3 w-full'>
            <DebouncedSearchInput
              key={searchResetKey}
              defaultValue={search}
              placeholder='Search org, user email, token, or #task/#conv id…'
              delayMs={SEARCH_DEBOUNCE_MS}
              onDebouncedChange={handleSearchChange}
            />
            <span className='text-xs text-tokens-neutral-text-secondary tabular-nums'>
              {isLoading ? '…' : `Showing ${items.length}`}
              {totalInWindow !== null && ` of ${totalInWindow} in window`}
            </span>
            {hasActiveFilters && (
              <Button
                size='sm'
                variant='light'
                onPress={clearFilters}
                aria-label='Clear all filters'
              >
                Clear filters
              </Button>
            )}
          </div>
        </CardHeader>
        <CardBody className='p-0'>
          {isLoading ? (
            <div className='p-4'>
              <Spinner size='sm' />
            </div>
          ) : isError ? (
            <div className='p-4 text-sm text-tokens-status-error-text'>
              Failed to load usage events: {error?.message ?? 'Unknown error'}
            </div>
          ) : items.length === 0 ? (
            <div className='p-4 text-sm text-tokens-neutral-text-secondary'>
              No events match the current filters.
            </div>
          ) : (
            <Table
              removeWrapper
              aria-label='Usage events'
              isHeaderSticky
              selectionMode='single'
              onRowAction={key => {
                const target = items.find(it => String(it.id) === String(key))
                if (target) setSelectedEvent(target)
              }}
            >
              <TableHeader>
                <TableColumn>When</TableColumn>
                <TableColumn>Source</TableColumn>
                <TableColumn>Metric</TableColumn>
                <TableColumn>Org</TableColumn>
                <TableColumn>Actor</TableColumn>
                <TableColumn>Detail</TableColumn>
              </TableHeader>
              <TableBody emptyContent='No events match the current filters.'>
                {items.map(item => {
                  const detailPieces = buildDetailPieces(item)
                  return (
                    <TableRow
                      key={item.id}
                      aria-label={`Usage event ${item.id}: ${item.metric_name}`}
                      className='cursor-pointer hover:bg-tokens-neutral-bg-grey-2'
                    >
                      <TableCell
                        className='text-xs whitespace-nowrap'
                        title={formatAbsolute(item.created_at_unix)}
                      >
                        {formatRelative(item.created_at_unix)}
                      </TableCell>
                      <TableCell>
                        <Chip size='sm' variant='flat' color={SOURCE_COLOR[item.source]}>
                          {SOURCE_LABEL[item.source]}
                        </Chip>
                      </TableCell>
                      <TableCell className='font-mono text-xs'>{item.metric_name}</TableCell>
                      <TableCell className='text-xs'>
                        <span title={`org_id ${item.org_id}`}>
                          {item.org_name ?? `Org ${item.org_id}`}
                        </span>
                      </TableCell>
                      <TableCell className='text-xs'>
                        {item.user_email ? (
                          <span title={item.user_id ? `user_id ${item.user_id}` : undefined}>
                            {item.user_email}
                          </span>
                        ) : item.api_token_truncated ? (
                          <span
                            className='font-mono'
                            title={
                              item.api_token_id ? `api_token_id ${item.api_token_id}` : undefined
                            }
                          >
                            {item.api_token_truncated}
                          </span>
                        ) : item.user_id !== null ? (
                          // user_id without an email row (rare — deleted user
                          // or pre-email signup). Surface the id so super-admin
                          // can still trace it.
                          <span className='text-tokens-neutral-text-secondary'>
                            user #{item.user_id}
                          </span>
                        ) : (
                          <span className='text-tokens-neutral-text-secondary'>—</span>
                        )}
                      </TableCell>
                      <TableCell className='text-xs'>
                        {detailPieces.length === 0 ? (
                          <span className='text-tokens-neutral-text-secondary'>—</span>
                        ) : (
                          <span className='inline-flex items-center gap-2'>{detailPieces}</span>
                        )}
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          )}
          {/* Load more — sits below the table when the API has more pages.
              Hidden during the initial load (the spinner above already
              communicates work in progress) and once we've reached the end. */}
          {!isLoading && !isError && items.length > 0 && hasNextPage && (
            <div className='p-3 flex justify-center border-t border-tokens-neutral-border-2'>
              <Button
                size='sm'
                variant='flat'
                onPress={() => fetchNextPage()}
                isLoading={isFetchingNextPage}
                aria-label='Load more usage events'
              >
                {isFetchingNextPage ? 'Loading…' : 'Load more'}
              </Button>
            </div>
          )}
        </CardBody>
      </Card>

      <DetailModal event={selectedEvent} onClose={() => setSelectedEvent(null)} />
    </div>
  )
}

export default UsageEventsTab
